import React, { useState } from "react";
import { BaseComponentImpl } from "../../types/implementations";
import { Building } from "../../types/protocols";
import ComponentButtonList from "./ComponentButtonList";

const MAX_VISIBLE = 5;

interface ComponentGroup {
    components: BaseComponentImpl[];
    defaultImagePath: string;
    getMetadata?: (c: BaseComponentImpl) => string[];
}

interface ExpandableComponentSectionProps {
    title: string;
    groups: ComponentGroup[];
    building: Building;
    selectedComponentKey: string | null;
    hoveredKey: string | null;
    onComponentClick: (key: string) => void;
    onMouseEnter: (key: string) => void;
    onMouseLeave: () => void;
}

const ExpandableComponentSection: React.FC<ExpandableComponentSectionProps> = ({
    title,
    groups,
    building,
    selectedComponentKey,
    hoveredKey,
    onComponentClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const [expanded, setExpanded] = useState(false);

    const allComponents = groups.flatMap(g => g.components);
    const totalCount = allComponents.length;
    const hasMore = totalCount > MAX_VISIBLE;

    if (totalCount === 0) return null;

    // Build visible groups based on expansion state
    const getVisibleGroups = (): ComponentGroup[] => {
        if (expanded || !hasMore) return groups;

        let remaining = MAX_VISIBLE;
        const visibleGroups: ComponentGroup[] = [];

        for (const group of groups) {
            if (remaining <= 0) break;

            if (group.components.length <= remaining) {
                visibleGroups.push(group);
                remaining -= group.components.length;
            } else {
                visibleGroups.push({
                    ...group,
                    components: group.components.slice(0, remaining),
                });
                remaining = 0;
            }
        }

        return visibleGroups;
    };

    const visibleGroups = getVisibleGroups();
    const hiddenCount = totalCount - MAX_VISIBLE;

    return (
        <div>
            <h4 style={{ marginBottom: '20px' }}>{title}</h4>
            <ul style={{ listStyleType: 'none', padding: 0 }}>
                {visibleGroups.map((group, index) => (
                    <ComponentButtonList
                        key={index}
                        components={group.components}
                        building={building}
                        defaultImagePath={group.defaultImagePath}
                        selectedComponentKey={selectedComponentKey}
                        hoveredKey={hoveredKey}
                        onComponentClick={onComponentClick}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        getMetadata={group.getMetadata}
                    />
                ))}
            </ul>
            {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <button
                        onClick={() => setExpanded(!expanded)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#555',
                            cursor: 'pointer',
                            padding: '6px 0',
                            fontSize: '13px',
                        }}
                    >
                        {expanded ? 'Visa färre ▲' : `Visa ${hiddenCount} till ▼`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ExpandableComponentSection;
